import { redirect, redirectDocument, Outlet } from 'react-router-dom';
import { datadogRum } from '@datadog/browser-rum';
import { initialize as initializeAmplitude, trackEvent } from 'lib/amplitude';
import { initialize as initializeDatadog } from 'lib/datadog';
import api, { getLogin } from 'api';
import CanvasAI from './CanvasAI';
import Dashboard from './Dashboard';
import Dashboards from './Dashboards';
import NotFound from './NotFound';
import Organization from './Organization';
import Project from './Project';
import Home from './Home';

/*
 * The /canvas-ai route displays uses a hard-coded topic ID to display a specific QnA topic
 */
const CANVAS_TOPIC_ID = 'FPrG1d0vRnXponu6Cc05I2tavydKw5QU';

export default [
  {
    /**
     * providing Outlet as the element for routes that render nothing silences a really annoying
     * warning from react-router that pollutes our test output without actually providing value
     *
     * The warning states that the "matched leaf route does not have an element
     * and will render a blank page", which, yeah, obviously. We're rendering
     * nothing, intentionally.
     */
    element: <Outlet />,
    path: '/:buildIdentifier?',
    /** this id allows us to access this route's loader data from any route */
    id: 'root',
    loader: async ({ request }) => {
      /**
       * checking auth here and returning the user object allows us to guard
       * from the root route; we only render the <Outlet /> if the user is logged in,
       * and we won't trigger the loader for the child routes if the content isn't rendered
       */
      let user;
      try {
        user = await api.getUser();
        initializeAmplitude(user.username);
        initializeDatadog(user);
      } catch {
        return redirectDocument(getLogin(request.url));
      }

      if (window.Intercom) {
        const intercomSettings = {
          app_id: import.meta.env.UF_INTERCOM_APP_ID,
          name: user.username,
          email: user.email,
          user_hash: user.intercom_user_hash,
          alignment: 'left',
          vertical_padding: 35,
          horizontal_padding: 10,
        };
        window.Intercom('boot', intercomSettings);
      }

      const portfolios = await api.getUserPortfolios(user.key);

      return { portfolios, user };
    },
    /**
     * since this is the initialization of the app, we never need to load this
     * data again once the app is running, so opt out of revalidation altogether
     */
    shouldRevalidate: () => false,
    children: [
      /* Q Anon */
      {
        element: <CanvasAI />,
        path: 'canvas-ai',
        loader: async () => {
          const topic = await api.getQnATopic(CANVAS_TOPIC_ID);
          return topic;
        },
      },
      /**
       * Anything that displays the "select a dashboard" header should render as a child of this route
       */
      {
        element: <Dashboards />,
        errorElement: <NotFound />,
        path: 'organization/:orgKey',
        id: 'org',
        loader: async ({ params }) => {
          let organization;
          let projects;

          try {
            projects = await api.getDashboards(params.orgKey);
            organization = await api.getOrganization(params.orgKey);
          } catch (error) {
            /**
             * Return an empty projects array if the user has no defined
             * dashboards so that dashboards built by hand can be viewed by
             * organizations that have not yet used LAS.
             */
            if (error.status === 404) {
              projects = [];
            }
          }

          /**
           * If we've been given a project key, redirect to the first dashboard in
           * that project. NOTE: we need to handle this here, because react-router
           * has NO MECHANISM FOR SHARING BETWEEN DATA LOADERS. If we use a loader
           * on the project route, we would need to perform the fetch for projects
           * again just to run the logic below.
           */
          if (params.projectKey) {
            const project = projects.find(
              ({ key }) => key === params.projectKey,
            );

            if (project?.dashboards.length > 0) {
              return redirect(
                `/organization/${params.orgKey}/dashboard/${project.dashboards[0].id}`,
              );
            }
            /* if the org has no projects, redirect to the org root page */
            if (projects.length === 0) {
              return redirect(`/organization/${params.orgKey}`);
            }
          }

          return { projects, organization };
        },
        children: [
          /* route used when switching orgs */
          {
            index: true,
            element: <Organization />,
          },
          /* route used when landing on a project with no dashboards */
          {
            path: 'project/:projectKey',
            element: <Project />,
            loader: async ({ params }) => {
              let organization;
              let project;
              let projectTypes;
              try {
                organization = await api.getOrganization(params.orgKey);
                project = await api.getProject({
                  org: params.orgKey,
                  project: params.projectKey,
                });
                projectTypes = await api.getProjectTypes(params.orgKey);
                if (!projectTypes) {
                  throw new Error(
                    `Failed to fetch project types for org: ${params.orgKey}`,
                  );
                }
              } catch (error) {
                datadogRum.addError(`Failed to fetch project data: ${error}`);
              }
              const projectType = projectTypes.items.find(
                (type) => type.key === project.project_type,
              );
              return { organization, project, projectType };
            },
          },
          {
            path: 'dashboard/:dashboardId',
            element: <Dashboard />,
            id: 'dashboard',
            loader: async ({ params }) => {
              const dashboard = await api.getDashboardURL({
                id: params.dashboardId,
                org: params.orgKey,
              });
              if (dashboard?.dashboard_id === params.dashboardId) {
                const {
                  dashboard_name: dashboardName,
                  input_layer_name: inputLayerName,
                  insight_key: insightKey,
                  project_key: projectKey,
                  project_type: projectType,
                } = dashboard;
                trackEvent('Dashboard Viewed', {
                  dashboardId: params.dashboardId,
                  orgKey: params.orgKey,
                  dashboardName,
                  inputLayerName,
                  insightKey,
                  projectKey,
                  projectTypeKey: projectType?.key,
                });
              }
              return dashboard;
            },
          },
          /* route used for handling dashboard updates */
          {
            element: <Outlet />,
            path: 'dashboard/:dashboardId/manage',
            action: async ({ params, request }) => {
              const formData = await request.formData();
              const name = formData.get('name');
              const returnToUrl = formData.get('returnTo');
              try {
                switch (request.method) {
                  case 'PUT': {
                    await api.updateDashboard({
                      id: params.dashboardId,
                      org: params.orgKey,
                      body: { name },
                    });
                    trackEvent('Dashboard Renamed', {
                      dashboardId: params.dashboardId,
                      dashboardName: name,
                    });
                    return redirect(returnToUrl);
                  }
                  case 'DELETE': {
                    await api.removeDashboard({
                      id: params.dashboardId,
                      org: params.orgKey,
                    });
                    trackEvent('Dashboard Deleted', {
                      dashboardId: params.dashboardId,
                      dashboardName: name,
                    });
                    return redirect(returnToUrl);
                  }
                  default: {
                    throw new Response('', { status: 405 });
                  }
                }
              } catch (error) {
                datadogRum.addError(`Failed to update dashboard: ${error}`);
              }
            },
          },
        ],
      },
      {
        element: <Home />,
        path: 'home',
      },
    ],
  },
];
